import { useNetworkDiagnostics } from '../../zustand/network-diagnostics-store';

/**
 * Temporary developer-only observability (network connectivity investigation,
 * Step 3). Supplements the axios-interceptor and MediaPipe-progress
 * instrumentation with what only the browser's own Resource Timing API can
 * see: DNS/TCP/TLS sub-timings for REST calls, and passive load timing for
 * `<img>` requests (which have no interceptor of their own to hook into).
 *
 * Entirely passive and read-only by spec — a PerformanceObserver cannot
 * change what was requested or how. Registered only when the debug panel
 * actually mounts (see network-diagnostics-panel), so it never runs for a
 * real patient session, and stopped on unmount.
 *
 * Known limitation, documented rather than worked around: DNS/TCP/TLS
 * sub-timings read back as 0 for a cross-origin response unless that origin
 * sends `Timing-Allow-Origin` (e.g. api.openai.com's SDP endpoint) — this is
 * a browser security restriction, not a bug here.
 */
let observer = null;

export function startResourceTimingObserver() {
  if (observer || typeof PerformanceObserver === 'undefined') {
    return () => {};
  }

  const record = useNetworkDiagnostics.getState().record;

  observer = new PerformanceObserver(list => {
    try {
      list.getEntries().forEach(entry => {
        const dnsMs = Math.round(entry.domainLookupEnd - entry.domainLookupStart);
        const connectMs = Math.round(entry.connectEnd - entry.connectStart);
        const ttfbMs = Math.round(entry.responseStart - entry.requestStart);

        if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
          if (dnsMs > 0) {
            record('rest', 'dns_time', dnsMs, 'ms', { endpoint: entry.name });
          }
          if (connectMs > 0) {
            record('rest', 'connect_tls_time', connectMs, 'ms', { endpoint: entry.name });
          }
          if (ttfbMs > 0) {
            record('rest', 'ttfb', ttfbMs, 'ms', { endpoint: entry.name });
          }
        } else if (entry.initiatorType === 'img') {
          const durationMs = Math.round(entry.responseEnd - entry.startTime);
          if (durationMs > 0) {
            record('media', 'image_download_duration_ms', durationMs, 'ms', {
              endpoint: entry.name,
            });
          }
          // transferSize === 0 on an otherwise-complete entry is the standard
          // heuristic for "served from the browser HTTP cache" — not a
          // guarantee (an opaque cross-origin response reports 0 too), hence
          // "cache hit" is a label, not a certainty.
          if (entry.transferSize === 0 && entry.decodedBodySize > 0) {
            record('media', 'image_cache_hit', 1, 'count', { endpoint: entry.name });
          }
        }
      });
    } catch (_error) {
      // Never let instrumentation throw out of the observer callback.
    }
  });

  try {
    observer.observe({ type: 'resource', buffered: true });
  } catch (_error) {
    // Older browsers without `buffered` support — retry without it.
    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (_innerError) {
      observer = null;
    }
  }

  return stopResourceTimingObserver;
}

export function stopResourceTimingObserver() {
  observer?.disconnect();
  observer = null;
}
